import { z } from 'zod'
import { isBlob } from '@/db/blob'
import {
  inspectionSearchFields,
  normalizePhone,
  normalizeSearchText,
  toActiveKey,
} from '@/db/normalization'
import type {
  AuditLog,
  Client,
  Counter,
  CreateClientInput,
  CreateInspectionInput,
  CreateMachineInput,
  CreatePhotoInput,
  CreateSignatureInput,
  CreateTechnicianInput,
  Inspection,
  InspectionResult,
  Machine,
  Photo,
  PhotoPreview,
  Settings,
  Signature,
  Technician,
  UpdateClientInput,
  UpdateInspectionInput,
  UpdateMachineInput,
  UpdateTechnicianInput,
} from '@/db/types'

const requiredText = (maximum = 500) => z.string().trim().min(1).max(maximum)
const optionalText = (maximum = 2_000) => z.string().trim().max(maximum).optional()
const uuidSchema = z.uuid()
const timestampSchema = z.iso.datetime()
const civilDateSchema = z.iso.date()
const nonNegativeNumber = z.number().finite().nonnegative()
const activeKeySchema = z.union([z.literal(0), z.literal(1)])
const blobSchema = z.custom<Blob>(isBlob)

function addNormalizationIssues(
  value: {
    active: boolean
    activeKey: 0 | 1
  },
  context: z.core.$RefinementCtx,
) {
  if (value.activeKey !== toActiveKey(value.active)) {
    context.addIssue({
      code: 'custom',
      message: 'activeKey não corresponde ao estado active.',
      path: ['activeKey'],
    })
  }
}

export const clientSchema: z.ZodType<Client> = z
  .object({
    id: uuidSchema,
    code: z.string().regex(/^CLI-\d{6}$/),
    companyName: requiredText(200),
    normalizedCompanyName: requiredText(200),
    farmOrSite: z.string().max(200),
    normalizedFarmOrSite: z.string().max(200),
    contactName: requiredText(200),
    phone: requiredText(30),
    normalizedPhone: z.string().regex(/^\d{8,20}$/),
    email: z.email().max(254).optional(),
    document: optionalText(40),
    address: optionalText(500),
    notes: optionalText(5_000),
    active: z.boolean(),
    activeKey: activeKeySchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()
  .superRefine((value, context) => {
    addNormalizationIssues(value, context)
    if (value.normalizedCompanyName !== normalizeSearchText(value.companyName)) {
      context.addIssue({
        code: 'custom',
        message: 'Nome normalizado inválido.',
        path: ['normalizedCompanyName'],
      })
    }
    if (
      value.normalizedFarmOrSite !== '' &&
      value.normalizedFarmOrSite !== normalizeSearchText(value.farmOrSite)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Campo legado normalizado inválido.',
        path: ['normalizedFarmOrSite'],
      })
    }
    if (value.normalizedPhone !== normalizePhone(value.phone)) {
      context.addIssue({
        code: 'custom',
        message: 'Telefone normalizado inválido.',
        path: ['normalizedPhone'],
      })
    }
  })

export const machineSchema: z.ZodType<Machine> = z
  .object({
    id: uuidSchema,
    code: z.string().regex(/^MAQ-\d{6}$/),
    clientId: uuidSchema,
    equipment: requiredText(200),
    normalizedEquipment: requiredText(200),
    brand: requiredText(120),
    normalizedBrand: requiredText(120),
    model: requiredText(120),
    normalizedModel: requiredText(120),
    year: z.number().int().min(1900).max(2100).optional(),
    serialChassis: requiredText(150),
    normalizedSerialChassis: requiredText(150),
    currentHourmeter: nonNegativeNumber,
    notes: z.string().trim().max(5_000),
    photoId: uuidSchema.optional(),
    active: z.boolean(),
    activeKey: activeKeySchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()
  .superRefine((value, context) => {
    addNormalizationIssues(value, context)
    const normalizedFields = [
      ['normalizedEquipment', value.normalizedEquipment, value.equipment],
      ['normalizedBrand', value.normalizedBrand, value.brand],
      ['normalizedModel', value.normalizedModel, value.model],
      ['normalizedSerialChassis', value.normalizedSerialChassis, value.serialChassis],
    ] as const
    for (const [path, normalized, source] of normalizedFields) {
      if (normalized !== normalizeSearchText(source)) {
        context.addIssue({ code: 'custom', message: 'Valor normalizado inválido.', path: [path] })
      }
    }
  })

export const technicianSchema: z.ZodType<Technician> = z
  .object({
    id: uuidSchema,
    name: requiredText(200),
    normalizedName: requiredText(200),
    role: requiredText(120),
    phone: z.string().trim().max(30),
    normalizedPhone: z.string().regex(/^(?:\d{8,20})?$/),
    signatureId: uuidSchema.optional(),
    active: z.boolean(),
    activeKey: activeKeySchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()
  .superRefine((value, context) => {
    addNormalizationIssues(value, context)
    if (value.normalizedName !== normalizeSearchText(value.name)) {
      context.addIssue({
        code: 'custom',
        message: 'Nome normalizado inválido.',
        path: ['normalizedName'],
      })
    }
    if (value.normalizedPhone !== normalizePhone(value.phone)) {
      context.addIssue({
        code: 'custom',
        message: 'Telefone normalizado inválido.',
        path: ['normalizedPhone'],
      })
    }
  })

const clientSnapshotSchema = z
  .object({
    code: z.string().regex(/^CLI-\d{6}$/),
    companyName: requiredText(200),
    farmOrSite: z.string().max(200),
    contactName: requiredText(200),
    phone: requiredText(30),
    email: z.email().max(254).optional(),
    document: optionalText(40),
    address: optionalText(500),
  })
  .strict()

const machineSnapshotSchema = z
  .object({
    code: z.string().regex(/^MAQ-\d{6}$/),
    equipment: requiredText(200),
    brand: requiredText(120),
    model: requiredText(120),
    year: z.number().int().min(1900).max(2100).optional(),
    serialChassis: requiredText(150),
  })
  .strict()

const technicianSnapshotSchema = z
  .object({
    name: requiredText(200),
    role: requiredText(120),
    phone: z.string().trim().max(30),
  })
  .strict()

export const inspectionSchema: z.ZodType<Inspection> = z
  .object({
    id: uuidSchema,
    reportNumber: z.string().regex(/^RM-\d{6}$/),
    clientId: uuidSchema,
    machineId: uuidSchema,
    technicianId: uuidSchema,
    clientSnapshot: clientSnapshotSchema,
    machineSnapshot: machineSnapshotSchema,
    technicianSnapshot: technicianSnapshotSchema,
    normalizedClientName: requiredText(200),
    normalizedClientPhone: z.string().regex(/^\d{8,20}$/),
    normalizedFarmOrSite: z.string().max(200),
    normalizedMachineEquipment: requiredText(200),
    normalizedMachineBrand: requiredText(120),
    normalizedMachineModel: requiredText(120),
    normalizedSerialChassis: requiredText(150),
    normalizedTechnicianName: requiredText(200),
    inspectionDate: civilDateSchema,
    hourmeter: nonNegativeNumber,
    status: z.enum(['draft', 'completed', 'cancelled']),
    currentStep: z.number().int().min(1).max(7),
    diagnosis: z.string().max(100_000),
    priority: z.enum(['urgent_stop_immediately', 'schedule_within_15_days', 'monitor', 'pending']),
    authorization: z.enum(['yes', 'no', 'unset']),
    authorizationAt: timestampSchema.optional(),
    responsibleName: z.string().trim().max(200),
    clientSignatureWaiverReason: z.string().trim().max(2_000),
    clientSignatureWaiverConfirmedAt: timestampSchema.optional(),
    clientSignatureId: uuidSchema.optional(),
    technicianSignatureId: uuidSchema.optional(),
    revisionNumber: z.number().int().nonnegative(),
    reopenedAt: timestampSchema.optional(),
    reopenReason: optionalText(2_000),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    completedAt: timestampSchema.optional(),
    cancelledAt: timestampSchema.optional(),
    cancellationReason: optionalText(2_000),
    sourceInspectionId: uuidSchema.optional(),
    schemaVersion: z.number().int().positive(),
  })
  .strict()
  .superRefine((value, context) => {
    const expectedSearchFields = inspectionSearchFields(value)
    for (const [field, expected] of Object.entries(expectedSearchFields)) {
      const actual = value[field as keyof typeof expectedSearchFields]
      const legacyFarm =
        field === 'normalizedFarmOrSite' &&
        actual === normalizeSearchText(value.clientSnapshot.farmOrSite)
      if (actual !== expected && !legacyFarm) {
        context.addIssue({
          code: 'custom',
          message: 'Campo de pesquisa histórica inválido.',
          path: [field],
        })
      }
    }
    if (value.status === 'completed' && !value.completedAt) {
      context.addIssue({
        code: 'custom',
        message: 'Conclusão exige completedAt.',
        path: ['completedAt'],
      })
    }
    if (value.status === 'cancelled' && (!value.cancelledAt || !value.cancellationReason)) {
      context.addIssue({
        code: 'custom',
        message: 'Cancelamento exige data e motivo.',
        path: ['cancelledAt'],
      })
    }
  })

export const inspectionResultSchema: z.ZodType<InspectionResult> = z
  .object({
    id: uuidSchema,
    inspectionId: uuidSchema,
    checklistItemId: z.string().regex(/^[1-7]\.\d+$/),
    checklistItemLabel: requiredText(250),
    itemOrder: z.number().int().min(1).max(39),
    systemId: z.string().regex(/^[1-7]$/),
    systemLabel: requiredText(120),
    systemOrder: z.number().int().min(1).max(7),
    status: z.enum(['good', 'attention', 'critical', 'unanswered']),
    observation: z.string().trim().max(20_000),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()

const photoObjectSchema = z
  .object({
    id: uuidSchema,
    inspectionId: uuidSchema.optional(),
    machineId: uuidSchema.optional(),
    resultId: uuidSchema.optional(),
    category: z.enum(['item', 'general']),
    blob: blobSchema,
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    size: z.number().int().nonnegative(),
    caption: z.string().trim().max(1_000),
    order: z.number().int().nonnegative(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()

function validatePhotoOwner(value: CreatePhotoInput | Photo, context: z.core.$RefinementCtx) {
  if (Boolean(value.inspectionId) === Boolean(value.machineId)) {
    context.addIssue({
      code: 'custom',
      message: 'A foto deve pertencer a uma inspeção ou a uma máquina, exclusivamente.',
      path: ['inspectionId'],
    })
  }
  if (value.category === 'item' && (!value.inspectionId || !value.resultId)) {
    context.addIssue({
      code: 'custom',
      message: 'Foto de item exige inspeção e resultado.',
      path: ['resultId'],
    })
  }
  if ((value.category === 'general' || value.machineId) && value.resultId) {
    context.addIssue({
      code: 'custom',
      message: 'Foto geral ou de máquina não pode apontar para resultado.',
      path: ['resultId'],
    })
  }
}

export const photoSchema: z.ZodType<Photo> = photoObjectSchema.superRefine(validatePhotoOwner)

export const photoPreviewSchema: z.ZodType<PhotoPreview> = z
  .object({
    id: uuidSchema,
    photoId: uuidSchema,
    inspectionId: uuidSchema,
    resultId: uuidSchema.optional(),
    category: z.enum(['item', 'general']),
    thumbnailBlob: blobSchema,
    thumbnailMimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    thumbnailWidth: z.number().int().positive(),
    thumbnailHeight: z.number().int().positive(),
    thumbnailSize: z.number().int().positive(),
    fullMimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    fullWidth: z.number().int().positive(),
    fullHeight: z.number().int().positive(),
    fullSize: z.number().int().positive(),
    caption: z.string().trim().max(1_000),
    order: z.number().int().nonnegative(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.category === 'item' && !value.resultId) {
      context.addIssue({
        code: 'custom',
        message: 'Preview de item exige resultado.',
        path: ['resultId'],
      })
    }
    if (value.category === 'general' && value.resultId) {
      context.addIssue({
        code: 'custom',
        message: 'Preview geral não aponta para resultado.',
        path: ['resultId'],
      })
    }
  })

const signatureObjectSchema = z
  .object({
    id: uuidSchema,
    inspectionId: uuidSchema.optional(),
    technicianId: uuidSchema.optional(),
    type: z.enum(['client', 'technician']),
    blob: blobSchema,
    mimeType: z.enum(['image/png', 'image/jpeg']),
    createdAt: timestampSchema,
  })
  .strict()

function validateSignatureOwner(
  value: CreateSignatureInput | Signature,
  context: z.core.$RefinementCtx,
) {
  if (!value.inspectionId && !value.technicianId) {
    context.addIssue({
      code: 'custom',
      message: 'A assinatura precisa de um proprietário.',
      path: ['inspectionId'],
    })
  }
  if (value.type === 'client' && (!value.inspectionId || value.technicianId)) {
    context.addIssue({
      code: 'custom',
      message: 'Assinatura do cliente pertence exclusivamente à inspeção.',
      path: ['type'],
    })
  }
}

export const signatureSchema: z.ZodType<Signature> =
  signatureObjectSchema.superRefine(validateSignatureOwner)

export const settingsSchema: z.ZodType<Settings> = z
  .object({
    key: z.literal('app'),
    appVersion: z
      .string()
      .trim()
      .regex(/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/),
    companyName: requiredText(200),
    phone: requiredText(30),
    instagram: z.string().trim().max(100),
    email: z.email().max(254),
    logo: blobSchema.optional(),
    qrCodeTarget: z.string().trim().max(2_000),
    lastBackupAt: timestampSchema.optional(),
    photoMaxInputBytes: z
      .number()
      .int()
      .min(1024 * 1024)
      .max(25 * 1024 * 1024),
    photoMaxDimension: z.number().int().min(800).max(2400),
    photoJpegQuality: z.number().min(0.7).max(0.8),
    schemaVersion: z.number().int().positive(),
    updatedAt: timestampSchema,
  })
  .strict()

export const counterSchema: z.ZodType<Counter> = z
  .object({
    key: z.enum(['clientCode', 'machineCode', 'reportNumber']),
    value: z.number().int().nonnegative().max(999_999),
    updatedAt: timestampSchema,
  })
  .strict()

export const auditLogSchema: z.ZodType<AuditLog> = z
  .object({
    id: uuidSchema,
    entityType: z.enum([
      'client',
      'machine',
      'technician',
      'inspection',
      'inspectionResult',
      'photo',
      'signature',
      'settings',
      'demoData',
    ]),
    entityId: z.string().min(1).max(200),
    action: z.enum(['create', 'update', 'inactivate', 'delete', 'complete', 'cancel', 'reopen']),
    occurredAt: timestampSchema,
    changes: z.record(z.string(), z.unknown()),
  })
  .strict()

const createClientInputObjectSchema = z
  .object({
    companyName: requiredText(200),
    contactName: requiredText(200),
    phone: requiredText(30).refine((value) => /^\d{10,11}$/.test(normalizePhone(value))),
    email: z.email().max(254).optional(),
    document: optionalText(40),
    address: optionalText(500),
    notes: optionalText(5_000),
  })
  .strict()

export const createClientInputSchema: z.ZodType<CreateClientInput> = createClientInputObjectSchema

export const updateClientInputSchema: z.ZodType<UpdateClientInput> = createClientInputObjectSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Informe ao menos um campo.')

const createMachineInputObjectSchema = z
  .object({
    clientId: uuidSchema,
    equipment: requiredText(200),
    brand: requiredText(120),
    model: requiredText(120),
    year: z.number().int().min(1900).max(2100).optional(),
    serialChassis: requiredText(150),
    currentHourmeter: nonNegativeNumber,
    notes: z.string().trim().max(5_000),
    photoId: uuidSchema.optional(),
  })
  .strict()

export const createMachineInputSchema: z.ZodType<CreateMachineInput> =
  createMachineInputObjectSchema

export const updateMachineInputSchema: z.ZodType<UpdateMachineInput> =
  createMachineInputObjectSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'Informe ao menos um campo.')

const createTechnicianInputObjectSchema = z
  .object({
    name: requiredText(200),
    role: requiredText(120),
    phone: z
      .string()
      .trim()
      .max(30)
      .refine((value) => !value || /^\d{10,11}$/.test(normalizePhone(value))),
    signatureId: uuidSchema.optional(),
  })
  .strict()

export const createTechnicianInputSchema: z.ZodType<CreateTechnicianInput> =
  createTechnicianInputObjectSchema

export const updateTechnicianInputSchema: z.ZodType<UpdateTechnicianInput> =
  createTechnicianInputObjectSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'Informe ao menos um campo.')

export const createInspectionInputSchema: z.ZodType<CreateInspectionInput> = z
  .object({
    clientId: uuidSchema,
    machineId: uuidSchema,
    technicianId: uuidSchema,
    inspectionDate: civilDateSchema,
    hourmeter: nonNegativeNumber,
    diagnosis: z.string().max(100_000).optional(),
  })
  .strict()

export const updateInspectionInputSchema: z.ZodType<UpdateInspectionInput> = z
  .object({
    inspectionDate: civilDateSchema.optional(),
    hourmeter: nonNegativeNumber.optional(),
    diagnosis: z.string().max(100_000).optional(),
    authorization: z.enum(['yes', 'no', 'unset']).optional(),
    authorizationAt: timestampSchema.optional(),
    responsibleName: z.string().trim().max(200).optional(),
    clientSignatureWaiverReason: z.string().trim().max(2_000).optional(),
    clientSignatureWaiverConfirmedAt: timestampSchema.optional(),
    clientSignatureId: uuidSchema.optional(),
    technicianSignatureId: uuidSchema.optional(),
    currentStep: z.number().int().min(1).max(7).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Informe ao menos um campo.')

export const createPhotoInputSchema: z.ZodType<CreatePhotoInput> = photoObjectSchema
  .omit({ id: true, createdAt: true, updatedAt: true })
  .superRefine(validatePhotoOwner)

export const createSignatureInputSchema: z.ZodType<CreateSignatureInput> = signatureObjectSchema
  .omit({ id: true, createdAt: true })
  .superRefine(validateSignatureOwner)
